/**
 * Shared constants for showcase and publish components.
 * Role → color maps, privacy icons, tool categories.
 */

import { ShieldCheck, Globe2, KeyRound } from "lucide-react"

// ── SVG node colors (for workflow diagrams) ──

export const ROLE_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  环境: { bg: "#3B82F620", border: "#3B82F650", text: "#3B82F6" },
  数据: { bg: "#10B98120", border: "#10B98150", text: "#10B981" },
  研究: { bg: "#6366F120", border: "#6366F150", text: "#6366F1" },
  调研: { bg: "#6366F120", border: "#6366F150", text: "#6366F1" },
  采集: { bg: "#6366F120", border: "#6366F150", text: "#6366F1" },
  转录: { bg: "#8B5CF620", border: "#8B5CF650", text: "#8B5CF6" },
  整理: { bg: "#14B8A620", border: "#14B8A650", text: "#14B8A6" },
  组织: { bg: "#14B8A620", border: "#14B8A650", text: "#14B8A6" },
  协作: { bg: "#F59E0B20", border: "#F59E0B50", text: "#F59E0B" },
  设计: { bg: "#EC489920", border: "#EC489950", text: "#EC4899" },
  创作: { bg: "#EC489920", border: "#EC489950", text: "#EC4899" },
  整合: { bg: "#10B98120", border: "#10B98150", text: "#10B981" },
  交付: { bg: "#EF444420", border: "#EF444450", text: "#EF4444" },
  运营: { bg: "#F9731620", border: "#F9731650", text: "#F97316" },
  增长: { bg: "#F9731620", border: "#F9731650", text: "#F97316" },
  客服: { bg: "#06B6D420", border: "#06B6D450", text: "#06B6D4" },
  服务: { bg: "#06B6D420", border: "#06B6D450", text: "#06B6D4" },
  法律: { bg: "#64748B20", border: "#64748B50", text: "#64748B" },
  法务: { bg: "#64748B20", border: "#64748B50", text: "#64748B" },
  合规: { bg: "#64748B20", border: "#64748B50", text: "#64748B" },
  风控: { bg: "#EF444420", border: "#EF444450", text: "#EF4444" },
  安全: { bg: "#3B82F620", border: "#3B82F650", text: "#3B82F6" },
  文档: { bg: "#8B5CF620", border: "#8B5CF650", text: "#8B5CF6" },
  解析: { bg: "#8B5CF620", border: "#8B5CF650", text: "#8B5CF6" },
  量化: { bg: "#F59E0B20", border: "#F59E0B50", text: "#F59E0B" },
  策略: { bg: "#10B98120", border: "#10B98150", text: "#10B981" },
  交易: { bg: "#EF444420", border: "#EF444450", text: "#EF4444" },
  开发: { bg: "#3B82F620", border: "#3B82F650", text: "#3B82F6" },
  项目管理: { bg: "#F59E0B20", border: "#F59E0B50", text: "#F59E0B" },
  记录: { bg: "#64748B20", border: "#64748B50", text: "#64748B" },
  分析: { bg: "#6366F120", border: "#6366F150", text: "#6366F1" },
}

export const DEFAULT_ROLE_COLOR = { bg: "#64748B15", border: "#64748B40", text: "#64748B" }

// ── Tailwind role badge classes (for team member cards) ──

export const ROLE_BG: Record<string, string> = {
  环境: "bg-blue-500/10 text-blue-500",
  数据: "bg-emerald-500/10 text-emerald-500",
  研究: "bg-indigo-500/10 text-indigo-500",
  调研: "bg-indigo-500/10 text-indigo-500",
  采集: "bg-indigo-500/10 text-indigo-500",
  转录: "bg-violet-500/10 text-violet-500",
  整理: "bg-teal-500/10 text-teal-500",
  组织: "bg-teal-500/10 text-teal-500",
  协作: "bg-amber-500/10 text-amber-500",
  设计: "bg-pink-500/10 text-pink-500",
  创作: "bg-pink-500/10 text-pink-500",
  整合: "bg-emerald-500/10 text-emerald-500",
  交付: "bg-red-500/10 text-red-500",
  运营: "bg-orange-500/10 text-orange-500",
  增长: "bg-orange-500/10 text-orange-500",
  客服: "bg-cyan-500/10 text-cyan-500",
  服务: "bg-cyan-500/10 text-cyan-500",
  法律: "bg-slate-500/10 text-slate-500",
  法务: "bg-slate-500/10 text-slate-500",
  合规: "bg-slate-500/10 text-slate-500",
  风控: "bg-red-500/10 text-red-500",
  安全: "bg-blue-500/10 text-blue-500",
  解析: "bg-violet-500/10 text-violet-500",
  文档: "bg-violet-500/10 text-violet-500",
  量化: "bg-amber-500/10 text-amber-500",
  策略: "bg-emerald-500/10 text-emerald-500",
  交易: "bg-red-500/10 text-red-500",
  开发: "bg-blue-500/10 text-blue-500",
  分析: "bg-indigo-500/10 text-indigo-500",
  记录: "bg-slate-500/10 text-slate-500",
}

export const DEFAULT_ROLE_BG = "bg-secondary/50 text-muted-foreground"

// ── Privacy icons (for skill showcase) ──

export const PRIVACY_ICON: Record<string, typeof ShieldCheck> = {
  local: ShieldCheck,
  "local-optional": ShieldCheck,
  external: Globe2,
  "auth-required": KeyRound,
  "security-tool": ShieldCheck,
}

export const PRIVACY_COLOR: Record<string, string> = {
  local: "text-emerald-500",
  "local-optional": "text-emerald-500",
  external: "text-sky-500",
  "auth-required": "text-sky-500",
  "security-tool": "text-blue-500",
}

// ── Tool category labels ──

export const CATEGORY_LABEL: Record<string, string> = {
  feishu: "飞书",
  search: "搜索",
  data: "数据",
  ai: "AI",
  ppt: "PPT",
  file: "文件",
  general: "通用",
}
